import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Star, MessageSquare, Loader2, ChevronDown } from "lucide-react";
import { RatingDialog } from "./RatingDialog";
import { Link, useLocation } from "react-router-dom";
import { APP_NAME } from "@/lib/constants";
import { format, isValid } from "date-fns";

interface ReviewsSectionProps {
  systemId: string;
  systemOwnerId: string;
  session: any;
  userRating: any;
  allReviews: any[];
  totalReviews: number;
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

const safeFormat = (dateStr: string | null | undefined, formatStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return isValid(date) ? format(date, formatStr) : '';
};

export const ReviewsSection = ({
  systemId,
  systemOwnerId,
  session,
  userRating,
  allReviews,
  totalReviews,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: ReviewsSectionProps) => {
  const location = useLocation();

  return (
    <section className="animate-in fade-in duration-500">
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-slate-900">חוות דעת גולשים</h3>
          {totalReviews > 0 && (
            <span className="text-sm font-medium bg-slate-100 text-slate-600 px-2.5 py-0.5 rounded-full">
              {totalReviews}
            </span>
          )}
        </div>
        
        <div>
          {session ? (
            systemOwnerId === session.user.id ? (
              <span className="text-xs text-slate-400">לא ניתן לדרג מערכת שלך</span>
            ) : (
              <RatingDialog 
                systemId={systemId} 
                existingRating={userRating}
                trigger={<Button variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg">
                  {userRating ? "ערוך דירוג" : "כתוב ביקורת"}
                </Button>}
              />
            )
          ) : (
            <Link to="/login" state={{ from: location }} className="text-sm font-medium text-blue-600 hover:underline">התחבר כדי לדרג</Link>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-xl border border-slate-100 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-9 w-9 rounded-full bg-slate-100" />
                <div className="space-y-1.5">
                  <div className="h-4 w-24 bg-slate-100 rounded" />
                  <div className="h-3 w-16 bg-slate-100 rounded" />
                </div>
              </div>
              <div className="h-4 w-full bg-slate-50 rounded" />
            </div>
          ))
        ) : allReviews.length === 0 ? (
          <div className="bg-slate-50 rounded-2xl p-10 text-center border border-dashed border-slate-200">
            <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">עדיין אין ביקורות למערכת זו.</p>
            {session && systemOwnerId !== session.user.id && (
              <p className="text-sm text-slate-400 mt-1">היה הראשון לדרג ולעזור לאחרים!</p>
            )}
          </div>
        ) : (
          <>
            <div className="grid gap-4">
              {allReviews.map((rating: any) => (
                <div key={rating.id} className="bg-white p-5 md:p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                       <Avatar className="h-9 w-9 border border-slate-100">
                        <AvatarFallback className="bg-slate-50 text-slate-600 text-xs font-bold">
                          {rating.user?.display_name?.[0] || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-bold text-slate-900 text-sm">
                           {rating.user?.display_name || "משתמש רשום"}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {safeFormat(rating.created_at, 'dd/MM/yyyy')}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3 h-3 md:w-3.5 md:h-3.5 ${i < rating.stars ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                      ))}
                    </div>
                  </div>
                  {rating.comment && (
                    <p className="text-slate-600 text-sm md:text-base leading-relaxed ps-1 border-r-2 border-slate-50 pr-3">
                      {rating.comment}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {hasNextPage && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="ghost"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="text-slate-500 hover:text-slate-900 gap-2 h-10 px-6 rounded-xl hover:bg-slate-50"
                >
                  {isFetchingNextPage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  {isFetchingNextPage 
                    ? "טוען..." 
                    : `הצג עוד ביקורות (${totalReviews - allReviews.length})`
                  }
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};